import axios from 'axios';

import { API_BASE_URL, AXIOS_CONFIG } from '../config/api';
import { Accommodation, Service, UserPreferences } from '../types';
import cognitoAuthService from './cognitoAuthService';

interface ChatResponse {
  response: string;
  session_id: string;
  tool_calls?: Array<{
    tool_name: string;
    arguments: string;
  }>;
}

interface ChatHistoryItem {
  id: number;
  session_id: string;
  message: string;
  response: string;
  tool_calls?: any;
  context?: any;
  created_at: string;
  message_type?: string;
  accommodations?: Accommodation[];
  services?: Service[];
  // Enhanced fields for rich message preservation
  message_structure?: any; // Complete frontend message objects
  carousel_states?: { [messageId: number]: number }; // Carousel positions
  message_metadata?: {
    has_recommendations: boolean;
    recommendation_types: string[];
    original_message_count: number;
  };
}

interface ChatSession {
  session_id: string;
  title: string;
  created_at: string;
  updated_at: string;
  message_count: number;
  latest_message?: string; // For backward compatibility
}

interface ChatData {
  properties?: Accommodation[];
  services?: Service[];
}

class ChatService {
  private apiClient = axios.create(AXIOS_CONFIG);

  private async checkNetworkConnectivity(): Promise<boolean> {
    try {
      // Quick health check to backend
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout for connectivity check

      const response = await fetch(`${API_BASE_URL}/api/v1/health`, {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return response.status === 200 || response.status === 404; // 404 is ok, means server is reachable
    } catch (error) {
      return false;
    }
  }

  private flattenDetailedAmenities(detailedAmenities: any): string[] {
    // Handle both detailed_amenities JSON object and old amenities string
    if (typeof detailedAmenities === 'string') {
      try {
        // Try to parse as JSON first
        const parsed = JSON.parse(detailedAmenities);
        return this.extractAmenitiesFromJson(parsed);
      } catch {
        // Fall back to comma-separated string parsing
        return detailedAmenities
          .split(',')
          .map((a: string) => a.trim())
          .filter(Boolean);
      }
    } else if (typeof detailedAmenities === 'object' && detailedAmenities !== null) {
      return this.extractAmenitiesFromJson(detailedAmenities);
    }

    return [];
  }

  private extractAmenitiesFromJson(amenitiesObj: Record<string, string[]>): string[] {
    const allAmenities: string[] = [];

    // Extract amenities from all categories (Kitchen, Bathroom, Entertainment, etc.)
    for (const category in amenitiesObj) {
      if (Array.isArray(amenitiesObj[category])) {
        allAmenities.push(...amenitiesObj[category]);
      }
    }

    return allAmenities;
  }

  private async getUserPreferences(): Promise<UserPreferences | null> {
    try {
      const response = await this.apiClient.get('/api/v1/users/preferences');
      return response.data;
    } catch (error) {
      return null;
    }
  }

  private scoreAccommodation(accommodation: Accommodation, preferences: UserPreferences): number {
    let score = 0;
    const maxScore = 100;

    // Budget match (40% of score)
    if (
      preferences.min_budget_per_night &&
      preferences.max_budget_per_night &&
      accommodation.base_price_per_night
    ) {
      const price = accommodation.base_price_per_night;
      if (price >= preferences.min_budget_per_night && price <= preferences.max_budget_per_night) {
        score += 40; // Perfect budget match
      } else if (
        price < preferences.max_budget_per_night * 1.2 &&
        price > preferences.min_budget_per_night * 0.8
      ) {
        score += 20; // Close to budget range
      }
    }

    // Location match (30% of score)
    if (preferences.preferred_locations && accommodation.location) {
      const matchingLocation = preferences.preferred_locations.find((loc) =>
        accommodation.location.toLowerCase().includes(loc.toLowerCase()),
      );
      if (matchingLocation) {
        score += 30;
      }
    }

    // Accommodation type match (20% of score)
    if (preferences.preferred_accommodation_types && accommodation.type) {
      if (preferences.preferred_accommodation_types.includes(accommodation.type)) {
        score += 20;
      }
    }

    // Rating bonus (10% of score)
    if (accommodation.average_rating) {
      if (accommodation.average_rating >= 4.5) {
        score += 10;
      } else if (accommodation.average_rating >= 4.0) {
        score += 5;
      }
    }

    return Math.min(score, maxScore);
  }

  private scoreService(service: Service, preferences: UserPreferences): number {
    let score = 0;
    const maxScore = 100;

    // Location match (40% of score)
    if (preferences.preferred_locations && service.location) {
      const matchingLocation = preferences.preferred_locations.find((loc) =>
        service.location.toLowerCase().includes(loc.toLowerCase()),
      );
      if (matchingLocation) {
        score += 40;
      }
    }

    // Service type match (30% of score)
    if (preferences.preferred_service_types && service.service_type) {
      if (preferences.preferred_service_types.includes(service.service_type)) {
        score += 30;
      }
    }

    // Travel style alignment (20% of score)
    if (preferences.travel_style) {
      const style = preferences.travel_style.toLowerCase();
      if (style === 'luxury' && service.average_rating && service.average_rating >= 4.5) {
        score += 20;
      } else if (style === 'business' && service.service_type === 'transportation') {
        score += 15;
      } else if (style === 'adventure' && service.service_type === 'activities') {
        score += 20;
      }
    }

    // Rating bonus (10% of score)
    if (service.average_rating) {
      if (service.average_rating >= 4.5) {
        score += 10;
      } else if (service.average_rating >= 4.0) {
        score += 5;
      }
    }

    return Math.min(score, maxScore);
  }

  private filterAndRankAccommodations(
    accommodations: Accommodation[],
    preferences: UserPreferences | null,
  ): Accommodation[] {
    if (!preferences) {
      return accommodations.slice(0, 8); // Return first 8 if no preferences
    }

    // Score all accommodations
    const scoredAccommodations = accommodations.map((accommodation) => ({
      accommodation,
      score: this.scoreAccommodation(accommodation, preferences),
    }));

    // Filter with reasonable threshold
    const filtered = scoredAccommodations.filter((item) => item.score >= 5);

    if (filtered.length === 0) {
      // Fallback: return any accommodations with positive score
      const anyScore = scoredAccommodations.filter((item) => item.score > 0);
      if (anyScore.length > 0) {
        return anyScore
          .sort((a, b) => b.score - a.score)
          .slice(0, 8)
          .map((item) => item.accommodation);
      }
    }

    return filtered
      .sort((a, b) => b.score - a.score)
      .slice(0, 8) // Top 8 recommendations
      .map((item) => item.accommodation);
  }

  private filterAndRankServices(
    services: Service[],
    preferences: UserPreferences | null,
  ): Service[] {
    if (!preferences) {
      return services.slice(0, 8); // Return first 8 if no preferences
    }

    // Score all services
    const scoredServices = services.map((service) => ({
      service,
      score: this.scoreService(service, preferences),
    }));

    // Filter with reasonable threshold
    const filtered = scoredServices.filter((item) => item.score >= 5);

    if (filtered.length === 0) {
      // Fallback: return any services with positive score
      const anyScore = scoredServices.filter((item) => item.score > 0);
      if (anyScore.length > 0) {
        return anyScore
          .sort((a, b) => b.score - a.score)
          .slice(0, 8)
          .map((item) => item.service);
      }
    }

    return filtered
      .sort((a, b) => b.score - a.score)
      .slice(0, 8) // Top 8 recommendations
      .map((item) => item.service);
  }

  constructor() {
    // Add Cognito auth token to requests if available
    this.apiClient.interceptors.request.use(async (config) => {
      try {
        const token = await cognitoAuthService.getValidAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        // Chat can work without authentication, so don't log errors
      }
      return config;
    });
  }

  async sendMessage(
    message: string,
    sessionId?: string,
    taxPercentage?: number,
  ): Promise<ChatResponse> {
    try {
      const payload = {
        message,
        session_id: sessionId,
        tax_percentage: taxPercentage,
      };

      const response = await this.apiClient.post('/api/v1/chat/', payload);

      return response.data;
    } catch (error: any) {
      console.error('Chat API error:', {
        error: error.message || error,
        sessionId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
      });
      throw error;
    }
  }

  async getChatHistory(sessionId?: string, limit: number = 50): Promise<ChatHistoryItem[]> {
    try {
      const response = await this.apiClient.get('/api/v1/chat/history', {
        params: { session_id: sessionId, limit },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to load chat history:', error);
      return [];
    }
  }

  async getChatSessions(): Promise<ChatSession[]> {
    try {
      const response = await this.apiClient.get('/api/v1/chat/sessions');
      return response.data;
    } catch (error) {
      console.error('Failed to load chat sessions:', error);
      return [];
    }
  }

  async clearChatSession(sessionId: string): Promise<boolean> {
    try {
      await this.apiClient.delete(`/api/v1/chat/history/${sessionId}`);
      return true;
    } catch (error) {
      console.error('Failed to clear chat session:', error);
      return false;
    }
  }

  async clearAllChatHistory(): Promise<boolean> {
    try {
      await this.apiClient.delete('/api/v1/chat/history');
      return true;
    } catch (error) {
      console.error('Failed to clear all chat history:', error);
      return false;
    }
  }

  async parseToolCalls(
    toolCalls?: Array<{ tool_name: string; arguments: string }>,
  ): Promise<ChatData> {
    const data: ChatData = {
      properties: [],
      services: [],
    };

    if (!toolCalls || toolCalls.length === 0) {
      return data;
    }

    // Fetch user preferences once for all filtering
    const userPreferences = await this.getUserPreferences();

    for (const toolCall of toolCalls) {
      try {
        const args = toolCall.arguments ? JSON.parse(toolCall.arguments) : {};

        switch (toolCall.tool_name) {
          case 'search_accommodations':
          case 'get_luxury_accommodations':
            // Map AI agent parameters to API parameters
            const apiParams: any = {};
            if (args.location) apiParams.location = args.location;
            if (args.search) apiParams.search = args.search;
            if (args.category) apiParams.category = args.category;
            // Map max_guests (minimum guests needed) to min_guests (API parameter)
            if (args.max_guests) apiParams.min_guests = args.max_guests;

            // Handle property_ids for follow-up questions with context
            let accommodationsResponse;
            if (args.property_ids && Array.isArray(args.property_ids)) {
              // Fetch all accommodations and filter by IDs
              const allAccommodations = await this.apiClient.get('/api/v1/accommodations/');
              accommodationsResponse = {
                data: allAccommodations.data.filter((item: any) =>
                  args.property_ids.includes(item.id),
                ),
              };
            } else {
              // Normal fetch with parameters
              accommodationsResponse = await this.apiClient.get('/api/v1/accommodations/', {
                params: apiParams,
              });
            }

            // Transform API data to match frontend interface (same as apiService.ts)
            const transformedAccommodations = accommodationsResponse.data.map((item: any) => ({
              id: item.id,
              name: item.name,
              description: item.description,
              location: item.location,
              base_price_per_night: parseFloat(
                item.base_price_per_night || item.price_per_night || 0,
              ),
              price_per_night: parseFloat(item.base_price_per_night || item.price_per_night || 0),
              final_price_per_night: parseFloat(item.final_price_per_night || 0),
              max_guests: item.max_guests,
              amenities: this.flattenDetailedAmenities(item.detailed_amenities || item.amenities),
              category: item.type === 'Penthouse' ? 'luxury_accommodation' : 'private_home',
              owner_id: item.vendor_id,
              is_available: item.is_available,
              average_rating: item.average_rating || null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              // Enhanced fields
              neighborhood: item.neighborhood,
              nearby_attractions: item.nearby_attractions,
              detailed_amenities: item.detailed_amenities,
              brand_name: item.brand_name,
              brand_logo_url: item.brand_logo_url,
              brand_link: item.brand_link,
              check_in_time: item.check_in_time,
              check_out_time: item.check_out_time,
              house_rules: item.house_rules,
              images: item.primary_image_url
                ? [
                    {
                      id: 1,
                      url: item.primary_image_url,
                      alt_text: item.name,
                      is_primary: true,
                      display_order: 1,
                      accommodation_id: item.id,
                    },
                  ]
                : [],
            }));

            // Store ALL transformed accommodations for AI extraction
            // Don't filter yet - we need all properties available for matching
            data.properties = transformedAccommodations;
            break;

          case 'search_services':
          case 'get_trending_services':
            // Handle service_ids for specific mentioned services (parallel to property_ids)
            let servicesResponse;
            if (args.service_ids && Array.isArray(args.service_ids)) {
              // Fetch all services and filter by IDs
              const allServices = await this.apiClient.get('/api/v1/services/');
              servicesResponse = {
                data: allServices.data.filter((item: any) => args.service_ids.includes(item.id)),
              };
            } else {
              // Normal fetch with parameters
              servicesResponse = await this.apiClient.get('/api/v1/services/', {
                params: args,
              });
            }

            // Store ALL services for AI extraction
            // Don't filter yet - we need all services available for matching
            data.services = servicesResponse.data;
            break;
        }
      } catch (error) {
        console.error(`Error processing tool call ${toolCall.tool_name}:`, error);
      }
    }

    return data;
  }

  private extractMentionedProperties(
    aiResponse: string,
    allProperties: Accommodation[],
  ): Accommodation[] {
    const mentionedProperties: Accommodation[] = [];
    let extractedIds: number[] = [];

    // Method 1: Extract property IDs from AI response (e.g., "Aspen Mountain Penthouse (ID: 123)")
    const idMatches = aiResponse.match(/\(ID:\s*(\d+)\)/g);

    if (idMatches) {
      extractedIds = idMatches
        .map((match) => {
          const idMatch = match.match(/\(ID:\s*(\d+)\)/);
          return idMatch ? parseInt(idMatch[1]) : null;
        })
        .filter((id) => id !== null);

      for (const id of extractedIds) {
        const property = allProperties.find((p) => p.id === id);
        if (property) {
          mentionedProperties.push(property);
        }
      }
    }

    // Method 2: Fallback - Add properties by name matching for failed ID lookups
    const lowerResponse = aiResponse.toLowerCase();

    // For any IDs that failed, try to find by name
    const failedIds = extractedIds.filter((id) => !mentionedProperties.find((p) => p.id === id));

    // Always try numbered list extraction if no IDs were found
    if (extractedIds.length === 0 || failedIds.length > 0) {
      // Look for numbered lists and extract property names with improved patterns
      // Pattern 1: Standard numbered list (handles text continuing after name)
      const numberedMatches = aiResponse.match(/\d+\.\s*([^-\n]*?)(?:\s*-|$|\n)/g);

      // Pattern 2: Also try to match property names that appear at start of sentences
      const sentenceMatches = aiResponse.match(
        /(?:^|\.\s+)([A-Z][^.]*?(?:Suite|Chalet|Penthouse|Manor|Estate|Ranch|Villa|Hotel|Resort|Lodge))/gm,
      );

      const allMatches = [...(numberedMatches || []), ...(sentenceMatches || [])];

      if (allMatches.length > 0) {
        for (const match of allMatches) {
          let propertyText = match.replace(/^\d+\.\s*/, '').trim();
          // Remove common descriptive prefixes
          propertyText = propertyText.replace(/^(The\s+|A\s+)/i, '');
          // Try multiple matching strategies
          const property = allProperties.find((p) => {
            const propertyLower = propertyText.toLowerCase();
            const nameLower = p.name.toLowerCase();

            // Strategy 1: Direct name match (partial)
            if (propertyLower.includes(nameLower) || nameLower.includes(propertyLower)) {
              return true;
            }

            // Strategy 2: Match by significant keywords
            const propertyWords = propertyLower.split(/[\s\-]+/).filter((w) => w.length > 2);
            const nameWords = nameLower.split(/[\s\-]+/).filter((w) => w.length > 2);

            // Count matching words
            let matchingWords = 0;
            for (const word of propertyWords) {
              if (nameWords.some((nw) => nw.includes(word) || word.includes(nw))) {
                matchingWords++;
              }
            }

            // If 2 or more significant words match, consider it a match
            if (matchingWords >= 2) {
              return true;
            }

            // Strategy 3: Check for key property type words
            const propertyTypes = [
              'suite',
              'chalet',
              'penthouse',
              'manor',
              'estate',
              'ranch',
              'villa',
            ];
            const hasPropertyType = propertyTypes.some(
              (type) => propertyLower.includes(type) && nameLower.includes(type),
            );

            if (hasPropertyType) {
              // If both have the same property type, check for other matching words
              const otherWords = propertyWords.filter((w) => !propertyTypes.includes(w));
              const otherNameWords = nameWords.filter((w) => !propertyTypes.includes(w));

              for (const word of otherWords) {
                if (otherNameWords.some((nw) => nw.includes(word) || word.includes(nw))) {
                  return true;
                }
              }
            }

            return false;
          });

          if (property && !mentionedProperties.includes(property)) {
            mentionedProperties.push(property);
          }
        }
      }
    }

    // Method 3: If still missing properties, try exact name matches
    if (mentionedProperties.length < extractedIds.length) {
      for (const property of allProperties) {
        if (
          lowerResponse.includes(property.name.toLowerCase()) &&
          !mentionedProperties.includes(property)
        ) {
          mentionedProperties.push(property);
        }
      }
    }

    // FALLBACK: If we found very few properties but the response clearly mentions accommodations,
    // and we have tool call data, return some properties to ensure cards are shown
    if (mentionedProperties.length <= 1 && allProperties.length > 3) {
      const hasAccommodationKeywords =
        /(?:accommodation|property|suite|chalet|penthouse|villa|manor|ranch|estate|hotel|resort|luxury)/i.test(
          aiResponse,
        );
      const hasNumberedList = /\d+\.\s+\w+/.test(aiResponse);
      const seemsLikeAccommodationResponse = hasAccommodationKeywords || hasNumberedList;

      if (seemsLikeAccommodationResponse) {
        // Return the first 5 properties from tool calls as a fallback
        const fallbackProperties = allProperties.slice(0, 5);
        return fallbackProperties;
      }
    }

    return mentionedProperties;
  }

  private extractMentionedServices(aiResponse: string, allServices: Service[]): Service[] {
    const mentionedServices: Service[] = [];

    // Method 1: Extract service IDs from AI response (e.g., "Private Chef Service (ID: 456)")
    const idMatches = aiResponse.match(/\(ID:\s*(\d+)\)/g);
    if (idMatches) {
      const extractedIds = idMatches
        .map((match) => {
          const idMatch = match.match(/\(ID:\s*(\d+)\)/);
          return idMatch ? parseInt(idMatch[1]) : null;
        })
        .filter((id) => id !== null);

      for (const id of extractedIds) {
        const service = allServices.find((s) => s.id === id);
        if (service) {
          mentionedServices.push(service);
        }
      }

      if (mentionedServices.length > 0) {
        return mentionedServices;
      }
    }

    // Method 2: Fallback to name matching
    const lowerResponse = aiResponse.toLowerCase();
    for (const service of allServices) {
      if (lowerResponse.includes(service.name.toLowerCase())) {
        mentionedServices.push(service);
      }
    }

    return mentionedServices;
  }

  private extractContentTypeIdentifier(response: string): {
    contentType: 'accommodation' | 'service' | 'mixed' | null;
    cleanResponse: string;
  } {
    // Extract content type identifier from start of response
    const identifierRegex = /^\[CONTENT_TYPE:(accommodation|service|mixed)\]/;
    const match = response.match(identifierRegex);

    if (match) {
      const contentType = match[1] as 'accommodation' | 'service' | 'mixed';
      const cleanResponse = response.replace(identifierRegex, '').trim();
      return { contentType, cleanResponse };
    }

    return { contentType: null, cleanResponse: response };
  }

  async sendMessageWithData(
    message: string,
    sessionId?: string,
    taxPercentage?: number,
  ): Promise<{
    response: string;
    session_id: string;
    properties?: Accommodation[];
    services?: Service[];
    contentType?: 'accommodation' | 'service' | 'mixed' | null;
  }> {
    const chatResponse = await this.sendMessage(message, sessionId, taxPercentage);

    // Extract and remove content type identifier from AI response
    const { contentType, cleanResponse } = this.extractContentTypeIdentifier(chatResponse.response);

    // Use clean response (without identifier) for processing and display
    const processedChatResponse = { ...chatResponse, response: cleanResponse };

    const structuredData = await this.parseToolCalls(chatResponse.tool_calls);

    // Extract only properties/services mentioned in AI response (use original response with IDs)
    const mentionedProperties = this.extractMentionedProperties(
      chatResponse.response,
      structuredData.properties || [],
    );

    const mentionedServices = this.extractMentionedServices(
      chatResponse.response,
      structuredData.services || [],
    );

    // FALLBACK: If no properties were extracted but we have tool call results,
    // return ALL properties from tool calls (ensures carousel always shows results)
    let finalProperties =
      mentionedProperties.length > 0 ? mentionedProperties : structuredData.properties || [];

    // If we still have no properties but we detect accommodation keywords in the response,
    // and we have tool call data, assume all tool call properties should be shown
    if (
      finalProperties.length === 0 &&
      structuredData.properties &&
      structuredData.properties.length > 0
    ) {
      const hasAccommodationKeywords =
        /(?:accommodation|property|suite|chalet|penthouse|villa|manor|ranch|estate)/i.test(
          chatResponse.response,
        );
      const hasNumberedList = /\d+\.\s+\w+/.test(chatResponse.response);

      if (hasAccommodationKeywords || hasNumberedList) {
        finalProperties = structuredData.properties;
      }
    }

    // ADDITIONAL FALLBACK: If tool calls failed entirely (null) and we still have no properties
    // but AI response contains accommodation mentions with IDs, load all accommodations to match against
    // Skip this if we have tool_calls (even with context arguments) as that means the backend is working
    if (finalProperties.length === 0 && !chatResponse.tool_calls) {
      const hasIDPatterns = /\(ID:\s*\d+\)/g.test(chatResponse.response);
      const hasAccommodationKeywords =
        /(?:accommodation|property|suite|chalet|penthouse|villa|manor|ranch|estate)/i.test(
          chatResponse.response,
        );

      if (hasIDPatterns && hasAccommodationKeywords) {
        try {
          const allAccommodations = await this.apiClient.get('/api/v1/accommodations/');
          const formattedAccommodations = allAccommodations.data.map((item: any) => ({
            id: item.id,
            name: item.name,
            description: item.description,
            location: item.location,
            price_per_night: parseFloat(item.base_price_per_night || item.price_per_night || 0),
            final_price_per_night: parseFloat(item.final_price_per_night || 0),
            max_guests: item.max_guests,
            amenities: this.flattenDetailedAmenities(item.detailed_amenities || item.amenities),
            category: item.type === 'Penthouse' ? 'luxury_accommodation' : 'private_home',
            owner_id: item.vendor_id,
            is_available: item.is_available,
            average_rating: item.average_rating || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            neighborhood: item.neighborhood,
            nearby_attractions: item.nearby_attractions,
            detailed_amenities: item.detailed_amenities,
            years_hosting: item.years_hosting,
            brand_name: item.brand_name,
            brand_logo_url: item.brand_logo_url,
            brand_link: item.brand_link,
            check_in_time: item.check_in_time,
            check_out_time: item.check_out_time,
            house_rules: item.house_rules,
            vendor_tax_percentage: item.vendor_tax_percentage,
            type: item.type,
            base_price_per_night: item.base_price_per_night,
            primary_image_url: item.primary_image_url,
            images: item.images || [],
          }));

          // Re-run extractMentionedProperties with all accommodations
          const emergencyExtracted = this.extractMentionedProperties(
            chatResponse.response,
            formattedAccommodations,
          );

          finalProperties = emergencyExtracted;
        } catch (error) {
          console.error('Emergency accommodation loading failed:', error);
        }
      }
    }

    const finalServices =
      mentionedServices.length > 0 ? mentionedServices : structuredData.services || [];

    return {
      response: cleanResponse, // Return clean response without identifier
      session_id: chatResponse.session_id,
      properties: finalProperties,
      services: finalServices,
      contentType, // Add content type to response
    };
  }

  // Local storage helpers for offline persistence
  private getChatStorageKey(sessionId: string): string {
    return `belhop_chat_${sessionId}`;
  }

  saveMessagesToLocalStorage(
    sessionId: string,
    messages: any[],
    carouselStates?: { [messageId: number]: number },
  ): void {
    try {
      const key = this.getChatStorageKey(sessionId);
      const dataToSave = {
        messages,
        carouselStates: carouselStates || {},
        version: '1.1', // Version for data structure compatibility
        timestamp: Date.now(),
      };
      localStorage.setItem(key, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Failed to save messages to localStorage:', error);
    }
  }

  loadMessagesFromLocalStorage(sessionId: string): {
    messages: any[];
    carouselStates: { [messageId: number]: number };
    version?: string;
    timestamp?: number;
  } {
    try {
      const key = this.getChatStorageKey(sessionId);
      const stored = localStorage.getItem(key);

      if (!stored) {
        return { messages: [], carouselStates: {} };
      }

      const parsed = JSON.parse(stored);

      // Handle legacy format (just array of messages)
      if (Array.isArray(parsed)) {
        return {
          messages: parsed,
          carouselStates: {},
          version: 'legacy',
        };
      }

      // Handle new format with carousel states
      if (parsed.version === '1.1') {
        return {
          messages: parsed.messages || [],
          carouselStates: parsed.carouselStates || {},
          version: parsed.version,
          timestamp: parsed.timestamp,
        };
      }

      // Fallback for unknown format
      return {
        messages: parsed.messages || [],
        carouselStates: {},
        version: parsed.version || 'unknown',
        timestamp: parsed.timestamp,
      };
    } catch (error) {
      console.error('Failed to load messages from localStorage:', error);
      return { messages: [], carouselStates: {} };
    }
  }

  clearLocalStorageMessages(sessionId: string): void {
    try {
      const key = this.getChatStorageKey(sessionId);
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to clear localStorage messages:', error);
    }
  }

  async updateChatContext(
    sessionId: string,
    accommodations?: Accommodation[],
    services?: Service[],
  ): Promise<boolean> {
    try {
      const response = await this.apiClient.post('/api/v1/chat/update-context', {
        session_id: sessionId,
        accommodations: accommodations || null,
        services: services || null,
      });

      return true;
    } catch (error) {
      console.error('Failed to update chat context:', error);
      return false;
    }
  }
}

export const chatService = new ChatService();
export default chatService;
